import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Sun, CloudRain, Wind, Drop, ArrowRight, Newspaper, Package, Basket,
  Warning, Sparkle, MapPin, Plant, Bug, Calendar, Users, Notepad, ThermometerSimple,
} from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";
import { useAuth } from "@/contexts/AuthContext";
import { fpPath } from "@/lib/site";
import GuideHelperChip from "@/components/GuideHelperChip";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import SEOHead from "@/components/SEOHead";
import PhotoIdentify from "@/components/PhotoIdentify";
import { readLocation } from "@/components/LocationPill";

/**
 * FarmersToday — the unified "good morning" farm briefing.
 *
 * 10-tile pulse summary that folds every FarmersPulse tab into one screen:
 *   Temperature · Rain · Irrigation · Planting · Harvest · Pest Watch ·
 *   Weather · Nearby · News · My Farm.
 *
 * Reads location from the shared `np:user_location` store (set via the
 * FarmersLocationPill / breadcrumb) so it re-hydrates when the user
 * changes their country/state/city/village in the header.
 */
const LOCAL_KEY = "np:user_location";

const WEATHER_LABEL = {
  0: "Clear",  1: "Mostly clear", 2: "Partly cloudy", 3: "Overcast",
  45: "Fog",   48: "Rime fog",
  51: "Light drizzle", 61: "Light rain", 63: "Moderate rain", 65: "Heavy rain",
  71: "Light snow", 73: "Moderate snow", 75: "Heavy snow",
  95: "Thunderstorm", 96: "Storm w/ hail",
};

export default function FarmersToday() {
  const { user } = useAuth();
  const [loc, setLoc] = useState(() => {
    const s = readLocation();
    if (s?.lat && s?.lon) return { lat: s.lat, lon: s.lon, label: s.city || s.country };
    return null;
  });
  const [d, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Listen for header location changes.
  useEffect(() => {
    const onChange = () => {
      const s = readLocation();
      if (s?.lat && s?.lon) setLoc({ lat: s.lat, lon: s.lon, label: s.city || s.country });
      else setLoc(null);
    };
    window.addEventListener("np:location-changed", onChange);
    window.addEventListener("storage", (e) => e.key === LOCAL_KEY && onChange());
    return () => window.removeEventListener("np:location-changed", onChange);
  }, []);

  // Silent GPS auto-fill (browser-permission-granted only, no prompt).
  useEffect(() => {
    if (loc) return;
    if (!navigator.geolocation || !navigator.permissions) return;
    navigator.permissions.query({ name: "geolocation" }).then((p) => {
      if (p.state !== "granted") return;
      navigator.geolocation.getCurrentPosition(
        (p2) => setLoc({ lat: p2.coords.latitude, lon: p2.coords.longitude }),
        () => {},
        { timeout: 6000 },
      );
    }).catch(() => {});
  }, [loc]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (loc?.lat != null) { params.set("latitude", loc.lat); params.set("longitude", loc.lon); }
    http.get(`/farmers/today?${params}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load today's dashboard"))
      .finally(() => setLoading(false));
  }, [loc?.lat, loc?.lon]);

  const requestLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    navigator.geolocation.getCurrentPosition(
      (p) => setLoc({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => toast.error("Location permission declined"),
    );
  };

  const greeting = greetingByHour();
  const name = user?.name?.split(" ")[0];

  // Compose a readable narration of the whole briefing so ListenTranslateBar
  // can read the pulse aloud in any of 30 languages.
  const narration = useMemo(() => {
    if (!d) return "";
    const w = d.weather; const p = d.pulse || {};
    const parts = [
      `${greeting}${name ? ", " + name : ""}. Here is the pulse of your farm today.`,
    ];
    if (w?.temperature_c != null) {
      parts.push(`Temperature ${Math.round(w.temperature_c)} degrees Celsius, humidity ${w.humidity ?? "?"}%.`);
    }
    if (w?.today_rain_mm != null) parts.push(`${(w.today_rain_mm ?? 0).toFixed(1)} millimeters of rain expected today.`);
    if (p.irrigation)             parts.push(`Irrigation: ${p.irrigation.recommendation}. ${p.irrigation.reason}.`);
    if (p.planting?.count)        parts.push(`${p.planting.count} crops in your local planting window${p.planting.examples?.length ? ", including " + p.planting.examples.slice(0,3).join(", ") : ""}.`);
    if (p.harvest_local?.count)   parts.push(`${p.harvest_local.count} crops approaching harvest${p.harvest_local.examples?.length ? ", including " + p.harvest_local.examples.slice(0,3).join(", ") : ""}.`);
    if (p.pests?.count)           parts.push(`Pest watch: ${p.pests.count} nearby pest or disease reports.`);
    if (p.weather_alert)          parts.push(`Weather alert: ${p.weather_alert}.`);
    if (p.nearby_count)           parts.push(`${p.nearby_count} new farmer observations nearby.`);
    if (p.news_count)             parts.push(`${p.news_count} agriculture and weather news stories to browse.`);
    return parts.join(" ");
  }, [d, greeting, name]);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="farmers-today-page">
      <SEOHead
        title="Farmers Pulse Today — Your Daily Farm Dashboard | FarmersPulse™"
        description="Good morning briefing for your farm — weather, rainfall, irrigation, planting, harvest, pest watch, nearby reports and news, all on one screen."
        path="/today"
      />
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        <header>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#2D8659)]">
            <Sparkle size={12} weight="fill" className="inline mr-1" /> Farmers Pulse Today
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight" data-testid="farmers-today-greeting">
            {name ? `${greeting}, ${name}.` : `${greeting}.`}
          </h1>
          <p className="mt-2 text-[#3A3830]">Here's the pulse of your farm today.</p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <ListenTranslateBar text={narration} testidPrefix="farmers-today-tools" />
            <ShareButton
              title="Farmers Pulse Today"
              text="Here's the pulse of my farm today."
              variant="chip"
              testid="farmers-today-share"
            />
            <PhotoIdentify
              variant="chip"
              locationContext={loc?.lat ? `${loc.lat.toFixed(3)}, ${loc.lon.toFixed(3)}` : null}
              testid="today-photo-identify"
            />
            {!loc && (
              <button
                onClick={requestLocation}
                data-testid="farmers-today-locate"
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--np-brand,#2D8659)]/40 bg-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#2D8659)] hover:bg-[color:var(--np-brand,#2D8659)] hover:text-white"
              >
                <MapPin size={11} weight="fill" /> Use my location
              </button>
            )}
          </div>
        </header>

        {/* --------- 10-tile good-morning pulse grid --------- */}
        <section className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="farmers-today-pulse">
          <PulseTile
            testid="pulse-temperature" icon={ThermometerSimple} label="Temperature"
            emoji="🌡"
            value={d?.weather?.temperature_c != null ? `${Math.round(d.weather.temperature_c)}°C` : "—"}
            sub={d?.weather?.humidity != null ? `${humidLabel(d.weather.humidity)} · ${d.weather.humidity}% RH` : "share location"}
            guide="weather-today"
          />
          <PulseTile
            testid="pulse-rain" icon={CloudRain} label="Rain (24h)"
            emoji="🌧"
            value={d?.weather ? `${(d.weather.today_rain_mm ?? 0).toFixed(1)} mm` : "—"}
            sub={d?.weather?.precipitation_mm > 0 ? "Falling now" : "Dry now"}
            guide="rainfall"
          />
          <PulseTile
            testid="pulse-irrigation" icon={Drop} label="Irrigation"
            emoji="💧"
            value={cap(d?.pulse?.irrigation?.recommendation) || "—"}
            sub={d?.pulse?.irrigation?.reason || "Set your location"}
            guide="irrigation"
          />
          <PulseTile
            testid="pulse-planting" icon={Plant} label="Planting"
            emoji="🌱"
            value={d?.pulse?.planting?.count ? `${d.pulse.planting.count} crops` : "—"}
            sub={d?.pulse?.planting?.examples?.length ? d.pulse.planting.examples.slice(0,3).join(", ") : "Local sow window"}
            guide="what-to-plant"
            link={fpPath("/calendar")}
          />
          <PulseTile
            testid="pulse-harvest" icon={Basket} label="Harvest"
            emoji="🧺"
            value={d?.pulse?.harvest_local?.count ? `${d.pulse.harvest_local.count} crops` : "—"}
            sub={d?.pulse?.harvest_local?.examples?.length ? d.pulse.harvest_local.examples.slice(0,3).join(", ") : "Regional harvest"}
            guide="what-to-harvest"
            link={fpPath("/calendar")}
          />
          <PulseTile
            testid="pulse-pest" icon={Bug} label="Pest Watch"
            emoji="🐛"
            value={d?.pulse?.pests?.count != null ? `${d.pulse.pests.count} reports` : "—"}
            sub={d?.pulse?.pests?.examples?.length ? d.pulse.pests.examples[0] : "Within 50 km · 3 days"}
            guide="pest-disease-alerts"
            link={fpPath("/pest-heatmap")}
          />
          <PulseTile
            testid="pulse-weather" icon={Warning} label="Weather Alert"
            emoji="⚠️"
            value={d?.pulse?.weather_alert ? "Alert" : d?.weather?.weather_code != null ? WEATHER_LABEL[d.weather.weather_code] || "—" : "—"}
            sub={d?.pulse?.weather_alert || (d?.weather ? `${Math.round(d.weather.today_high_c ?? 0)}° / ${Math.round(d.weather.today_low_c ?? 0)}°` : "Set location")}
            guide="farm-alerts"
          />
          <PulseTile
            testid="pulse-nearby" icon={Users} label="Nearby"
            emoji="👨‍🌾"
            value={d?.pulse?.nearby_count != null ? `${d.pulse.nearby_count} reports` : "—"}
            sub="Within 50 km · last 3 days"
            guide="nearby-farmer-reports"
            link={fpPath("/report")}
          />
          <PulseTile
            testid="pulse-news" icon={Newspaper} label="News"
            emoji="📰"
            value={d?.pulse?.news_count != null ? `${d.pulse.news_count} stories` : "—"}
            sub="Agri · Weather · Food · Water"
            guide="agricultural-news"
            link="#farmers-today-news"
          />
          <PulseTile
            testid="pulse-myfarm" icon={Notepad} label="My Farm"
            emoji="📅"
            value={
              user
                ? d?.inventory?.reminders_due
                  ? `${d.inventory.reminders_due} reminders`
                  : d?.upcoming_harvests?.length
                    ? `${d.upcoming_harvests.length} due`
                    : "All clear"
                : "Sign in"
            }
            sub={user ? "Reminders + harvests" : "Track fields, animals, expenses"}
            guide="my-farm"
            link={user ? fpPath("/mine") : "/login"}
          />
        </section>

        {/* --------- Nearby reports strip --------- */}
        {d?.nearby_reports?.length > 0 && (
          <section className="mt-12" data-testid="farmers-today-nearby">
            <div className="flex items-end justify-between flex-wrap gap-2">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#2D8659)] flex items-center">
                  Nearby · last 3 days<GuideHelperChip slug="nearby-farmer-reports" />
                </div>
                <h2 className="mt-1 font-serif-display text-2xl">What your neighbours are reporting</h2>
              </div>
              <Link to={fpPath("/report")} className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#2D8659)] hover:text-[color:var(--np-brand,#2D8659)]">
                Add a report <ArrowRight size={10} weight="bold" className="inline ml-1" />
              </Link>
            </div>
            <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.nearby_reports.slice(0, 6).map((r) => (
                <div key={r.id} className="rounded-2xl border border-black/10 bg-white p-4">
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#2D8659)]">
                    {r.category}{r.severity && r.severity !== "unknown" ? ` · ${r.severity}` : ""}
                  </div>
                  <div className="mt-1 font-serif-display text-lg">{r.title || "(no title)"}</div>
                  {r.location?.city && (
                    <div className="mt-1 text-xs text-[#3A3830]"><MapPin size={10} weight="fill" className="inline mr-1" /> {r.location.city}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* --------- Local news feed (crawled) --------- */}
        {d?.news?.length > 0 && (
          <section id="farmers-today-news" className="mt-12" data-testid="farmers-today-news">
            <div className="flex items-end justify-between flex-wrap gap-2">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#2D8659)] flex items-center">
                  Local news · agriculture · weather · food<GuideHelperChip slug="agricultural-news" />
                </div>
                <h2 className="mt-1 font-serif-display text-2xl">Stories that touch your farm</h2>
              </div>
              <Link to={fpPath("/news")} data-testid="farmers-today-news-all" className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#2D8659)] hover:text-[color:var(--np-brand,#2D8659)]">
                All news <ArrowRight size={10} weight="bold" className="inline ml-1" />
              </Link>
            </div>
            <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.news.map((n) => (
                <NewsCard key={n.id} n={n} />
              ))}
            </div>
          </section>
        )}

        {loading && !d && (
          <div className="mt-10 text-[#3A3830]">Loading today's pulse…</div>
        )}
      </div>
    </main>
  );
}

function PulseTile({ testid, icon: Icon, label, emoji, value, sub, guide, link }) {
  const inner = (
    <div className="rounded-2xl border border-black/10 bg-white p-4 h-full hover:border-[color:var(--np-brand,#2D8659)] transition-colors">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[color:var(--np-brand,#2D8659)]">
          <span className="text-sm leading-none" aria-hidden>{emoji}</span>
          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</div>
        </div>
        <Icon size={13} weight="fill" className="text-[color:var(--np-brand,#2D8659)]/70" />
      </div>
      <div className="mt-2 font-serif-display text-2xl leading-tight text-[#151312]">{value}</div>
      {sub && <div className="mt-1 text-xs text-[#3A3830] line-clamp-2">{sub}</div>}
      {guide && (
        <div className="mt-3">
          <GuideHelperChip slug={guide} label="Listen to guide" />
        </div>
      )}
    </div>
  );
  return link ? (
    <Link to={link} data-testid={testid} className="block h-full">{inner}</Link>
  ) : (
    <div data-testid={testid} className="h-full">{inner}</div>
  );
}

function NewsCard({ n }) {
  return (
    <a
      href={n.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`farmers-today-news-${n.id}`}
      className="block rounded-2xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#2D8659)] hover:shadow-sm transition"
    >
      <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">
        {n.publisher || n.source_domain}{n.category ? ` · ${n.category}` : ""}
      </div>
      <div className="mt-1 font-serif-display text-base leading-tight">{n.headline || n.title}</div>
      {n.summary && <p className="mt-1 text-sm text-[#3A3830] line-clamp-2">{n.summary}</p>}
      <div className="mt-2 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#2D8659)]">
        Read <ArrowRight size={10} weight="bold" />
      </div>
    </a>
  );
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 5)  return "Late night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Late evening";
}

function humidLabel(rh) {
  if (rh >= 80) return "Very humid";
  if (rh >= 60) return "Humid";
  if (rh >= 40) return "Comfortable";
  return "Dry";
}

function cap(s) {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}
